import { type FC, useState } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import type { FlowScreen, FlowAnswers } from "@/shared/constants/flowTypes";
import { cn } from "@/shared/utils/core";
import { renderInlineMarkdown } from "@/shared/utils/inlineMarkdown";
import { usePerson } from "@/shared/hooks/useAppContext";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { RequestDocumentDialog } from "@/pages/onboarding/components/screens/RequestDocumentDialog";

const imageMaxHeightClasses = {
  xs: "max-h-[150px]",
  sm: "max-h-[200px]",
  md: "max-h-[275px]",
  lg: "max-h-[350px]",
} as const;

import { NoteCard } from "./NoteCard";
import { FullLayout } from "./layouts/FullLayout";
import { SplitLayout } from "./layouts/SplitLayout";
import { InputField } from "./questions/InputField";
import { MultipleChoice } from "./questions/MultipleChoice";
import { MultiSelect } from "./questions/MultiSelect";
import { TextField } from "./questions/TextField";

interface ValidationErrors {
  [fieldId: string]: string;
}

interface ScreenRendererProps {
  screen: FlowScreen;
  answers: FlowAnswers;
  errors: ValidationErrors;
  onAnswer: (questionId: string, value: string | string[]) => void;
  onContinue: (options?: { skipValidation?: boolean }) => void;
  onBack?: () => void;
  onGoToScreen: (screenId: string, updatedAnswers?: FlowAnswers) => void;
  onCompleteFlow?: () => void;
  isLastStep: boolean;
}

export const ScreenRenderer: FC<ScreenRendererProps> = ({
  screen,
  answers,
  errors,
  onAnswer,
  onContinue,
  onBack,
  onGoToScreen,
  onCompleteFlow,
}) => {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const person = usePerson();

  const renderQuestion = () => {
    if (!screen.question) return null;

    const currentAnswer = answers[screen.question.id];

    switch (screen.question.type) {
      case "multiple_choice": {
        const rawAnswer = typeof currentAnswer === "string" ? currentAnswer : undefined;
        const selectedValue = rawAnswer?.includes(":") ? rawAnswer.split(":")[0] : rawAnswer;
        const textInputValues: Record<string, string> = {};
        if (rawAnswer?.includes(":")) {
          textInputValues[selectedValue!] = rawAnswer.slice(selectedValue!.length + 1);
        }

        return (
          <MultipleChoice
            question={screen.question}
            value={selectedValue}
            onChange={(value) => onAnswer(screen.question!.id, value)}
            errorMessage={errors[screen.question.id]}
            textInputValues={textInputValues}
            onTextInputChange={(optionValue, text) =>
              onAnswer(screen.question!.id, text ? `${optionValue}:${text}` : optionValue)
            }
          />
        );
      }
      case "multi_select":
        return (
          <MultiSelect
            question={screen.question}
            value={Array.isArray(currentAnswer) ? currentAnswer : []}
            onChange={(value) => onAnswer(screen.question!.id, value)}
            errorMessage={errors[screen.question.id]}
          />
        );
      case "text_field":
        return (
          <TextField
            question={screen.question}
            value={typeof currentAnswer === "string" ? currentAnswer : ""}
            onChange={(value) => onAnswer(screen.question!.id, value)}
            errorMessage={errors[screen.question.id]}
          />
        );
      case "input_field":
        return (
          <InputField
            question={screen.question}
            values={answers}
            errors={errors}
            onChange={(fieldId, value) => onAnswer(fieldId, value)}
          />
        );
      case "custom":
        return null;
      default:
        return null;
    }
  };

  const handleNoteRequestComplete = (recipientName: string) => {
    const docType = screen.note?.requestDocumentType;
    const goTo = screen.note?.requestGoToScreenId;

    const updatedAnswers = {
      ...answers,
      ...(docType ? { [`requested-from-${docType}`]: recipientName } : {}),
      ...(screen.question ? { [screen.question.id]: "yes" } : {}),
    };

    if (goTo) {
      onGoToScreen(goTo, updatedAnswers);
    } else {
      if (docType) onAnswer(`requested-from-${docType}`, recipientName);
      if (screen.question) onAnswer(screen.question.id, "yes");
    }
  };

  const isCentered = screen.textAlign === "center";
  const isSubtitleCentered = (screen.subtitleAlign ?? screen.textAlign) === "center";
  const imageRight = screen.imagePosition === "right";

  const textContent = (
    <div className="flex flex-col">
      {screen.layout === "full" && screen.imageSrc && !imageRight && (
        <div className={cn("mb-5", isCentered && "flex justify-center")}>
          <img
            src={screen.imageSrc}
            alt={screen.title || "Screen illustration"}
            className={cn(
              "rounded-lg object-contain",
              isCentered ? undefined : "w-full",
              imageMaxHeightClasses[screen.imageSize ?? "lg"],
            )}
          />
        </div>
      )}

      {screen.icon && (
        <div className={`mb-4 ${isCentered ? "flex justify-center" : ""}`}>
          <screen.icon className={screen.iconClassName || "h-8 w-8"} />
        </div>
      )}

      {screen.sectionLabel && (
        <p className="text-2xs font-semibold uppercase tracking-widest text-secondary mb-2">
          {screen.sectionLabel}
        </p>
      )}

      {screen.title && <h2 className={isCentered ? "text-center" : undefined}>{screen.title}</h2>}

      {screen.subtitle && (
        <p className={`mt-2 text-secondary ${isSubtitleCentered ? "text-center text-pretty" : ""}`}>
          {renderInlineMarkdown(screen.subtitle)}
        </p>
      )}

      {screen.contentComponent && (
        <div className="mt-8">
          <screen.contentComponent
            onComplete={onContinue}
            onCompleteFlow={onCompleteFlow}
            answers={answers}
            setAnswer={onAnswer}
            goToScreen={onGoToScreen}
            errors={errors}
          />
        </div>
      )}

      {screen.question && (
        <div key={screen.question.id} className="mt-8">
          {renderQuestion()}
        </div>
      )}

      {screen.customComponent && (
        <div className="mt-8">
          <screen.customComponent
            onComplete={onContinue}
            onCompleteFlow={onCompleteFlow}
            answers={answers}
            setAnswer={onAnswer}
            goToScreen={onGoToScreen}
            errors={errors}
          />
        </div>
      )}

      {screen.note && screen.note.position === "above" && (
        <div className="mt-8 flex justify-center">
          <NoteCard content={screen.note.content} className={screen.note.className} />
        </div>
      )}

      {!screen.hideButton && (
        <div className="mt-10 flex flex-col items-center gap-3">
          <Button
            variant="brand"
            rounded={true}
            onClick={() => (screen.onButtonClick ? screen.onButtonClick() : onContinue())}
            className="w-full"
            disabled={screen.isButtonDisabled?.({ answers, currentStep: 0 }) ?? false}
          >
            {screen.getButtonText?.({ answers, currentStep: 0 }) ?? "Continue"}{" "}
            <ArrowRight className="h-4 w-4" />
          </Button>

          {screen.note?.variant === "action" && (
            <Button
              variant="link"
              className="text-secondary"
              onClick={() => {
                if (screen.note?.triggersComplete && onCompleteFlow) {
                  onCompleteFlow();
                } else if (screen.note?.setAnswerAndContinue) {
                  const { questionId, value } = screen.note.setAnswerAndContinue;
                  const updatedAnswers = { ...answers, [questionId]: value };
                  if (screen.note.goToScreenId) {
                    onGoToScreen(screen.note.goToScreenId, updatedAnswers);
                  } else {
                    onAnswer(questionId, value);
                    onContinue({ skipValidation: true });
                  }
                } else if (screen.note?.goToScreenId) {
                  onGoToScreen(screen.note.goToScreenId);
                } else {
                  onContinue({ skipValidation: true });
                }
              }}
            >
              {screen.note.content}
            </Button>
          )}
        </div>
      )}

      {screen.showEncryptionNote && (
        <SecurityNote
          className="mt-10"
          text="Bank-level encryption, and we never sell your information"
        />
      )}

      {screen.note && screen.note.position !== "above" && screen.note.variant !== "action" && (
        <div
          className={`mt-3 ${screen.note.variant === "info-box" ? "w-full" : screen.note.align === "left" ? "" : "flex justify-center"}`}
        >
          <NoteCard
            content={screen.note.content}
            variant={screen.note.variant === "info-box" ? "info-box" : undefined}
            align={screen.note.align}
            className={screen.note.className}
            cardIcon={screen.note.cardIcon}
            cardTitle={screen.note.cardTitle}
            cardSubtitle={screen.note.cardSubtitle}
            collapsible={screen.note.collapsible}
            helpItems={screen.note.helpItems}
            helpFeedbackKey={screen.note.helpFeedbackKey}
            showRequestLink={screen.note.showRequestLink}
            onRequestClick={
              screen.note.showRequestLink && screen.note.requestDocumentType && person?.estate_id
                ? () => setRequestDialogOpen(true)
                : undefined
            }
          />
        </div>
      )}
    </div>
  );

  const content = imageRight ? (
    <div className="flex flex-col sm:flex-row sm:gap-16 sm:items-center">
      <div className="flex-1">{textContent}</div>
      {screen.imageSrc && (
        <div className="hidden sm:flex flex-1 shrink-0 h-72 px-6 justify-center items-center">
          <img
            src={screen.imageSrc}
            alt={screen.title || "Screen illustration"}
            className="w-full h-full object-contain"
          />
        </div>
      )}
    </div>
  ) : (
    textContent
  );

  const noteDocType = screen.note?.requestDocumentType;
  const noteDocLabel = screen.note?.requestDocumentLabel;

  const layout =
    screen.layout === "split" ? (
      <SplitLayout
        key={screen.id}
        imageSrc={screen.imageSrc}
        showMobileImage={screen.showMobileImage}
        onBack={onBack}
      >
        {content}
      </SplitLayout>
    ) : (
      <FullLayout key={screen.id} maxWidth={screen.maxWidth} verticalCenter={screen.verticalCenter}>
        {content}
      </FullLayout>
    );

  if (!screen.note?.showRequestLink || !noteDocType || !person?.estate_id) {
    return layout;
  }

  return (
    <>
      {layout}
      <RequestDocumentDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        documentLabel={noteDocLabel || noteDocType}
        documentType={noteDocType}
        estateId={person.estate_id}
        firstName={person.first_name || "your loved one"}
        onRequestSent={(recipientName) => {
          setRequestDialogOpen(false);
          handleNoteRequestComplete(recipientName);
        }}
      />
    </>
  );
};
